import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex } from 'rebass/styled-components';
import useProjectsListener from 'hooks/useProjectsListener';
import ProjectFilter from 'components/projectFilter';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from 'store';
import { AddProject } from 'components/addProject';
import useProjects from 'hooks/useProjects';
import ProjectContainer from 'components/projectContainer';
import { push } from 'connected-react-router';
import { Spinner } from 'components/spinner';
import { DropDownMenu } from 'components/dropDownMenu';
import Slider from 'components/Slider';
import ProjectCount from 'components/ProjectCount';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Dialog } from 'components/dialog';
import { IncompleteList } from 'components/incompleteList';
import { setProject } from 'store/dataSlice';
import { DropdownItemsType } from 'util/types';
import { allProjects, attentionProjects, ATTENTION_LABEL, AttentionKind } from 'search/builtinSearch';
import useMainTags from 'hooks/useMainTags';
import { ui, button } from 'util/ui';
import { PlusIcon } from 'components/shell/Icons';
import DustBackground from 'components/shell/DustBackground';

// Task #14 (as clarified by Aaron, 5 Nov 2026): the menu lists the sort orders, then only the
// customer's main tags from Admin › Settings.
const TAG_PREFIX = 'tag:';
/** Gap between project cards, and between the search box and the first row of cards. */
const CARD_GAP = 14;

const sortValue = (p: IProject, field: string): string | number => {
  const v = (p as any)[field];
  if (field === 'date' || field === 'budget') return Number(v) || 0;
  return String(v || '').toLowerCase();
};

const sortProjects = (list: IProject[], field: string, direction: 'asc' | 'desc') =>
  list.slice().sort((a, b) => {
    const x = sortValue(a, field);
    const y = sortValue(b, field);
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return direction === 'asc' ? c : -c;
  });

const dropdownItems: DropdownItemsType[] = [
  { label: 'newest', value: 'date desc' },
  { label: 'oldest', value: 'date asc' },
  { label: 'title (a-z)', value: 'title asc' },
  { label: 'title (z-a)', value: 'title desc' },
  { label: 'client (a-z)', value: 'client asc' },
  { label: 'client (z-a)', value: 'client desc' },
  { label: 'budget low', value: 'budget asc' },
  { label: 'budget high', value: 'budget desc' },
  { label: 'agency (a-z)', value: 'agency asc' },
  { label: 'agency (z-a)', value: 'agency desc' },
];

const Home = () => {
  const {
    filteredProjects,
    projectsLoading,
    loadMore,
    loadAllProjects,
    setFilter,
    setSort,
    sort,
    active,
    total,
  } = useProjectsListener();
  const dispatch = useDispatch();
  const { addProject } = useProjects();
  const { incompleteProjects } = useProjectsListener();
  const [scale, setScale] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [searchedItems, setSearchedItems] = useState<IProject[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [taggedProjects, setTaggedProjects] = useState<IProject[]>([]);
  // Only the customer's main tags (Admin › Settings), not every tag ever typed on a project.
  const tagNames = useMainTags();
  const [tagError, setTagError] = useState('');

  // "Needs attention" lists from Admin › Overview arrive as /?attention=…
  const routeSearch: string = useSelector((state: any) => state.router?.location?.search || '');
  const attentionParam = new URLSearchParams(routeSearch).get('attention') || '';
  const attention = (attentionParam in ATTENTION_LABEL ? attentionParam : '') as AttentionKind | '';
  const [attentionList, setAttentionList] = useState<IProject[] | null>(null);
  useEffect(() => {
    if (!attention) {
      setAttentionList(null);
      return;
    }
    attentionProjects(attention)
      .then((list) => setAttentionList(sortProjects(list, sort.type, sort.direction as 'asc' | 'desc')))
      .catch((e) => setTagError((e as Error).message || 'Could not load that list.'));
  }, [attention, sort.type, sort.direction]);

  // Projects with the chosen tag, in the chosen sort order.
  useEffect(() => {
    if (!tagFilter) {
      setTaggedProjects([]);
      return;
    }
    allProjects()
      .then((projects) => {
        const hits = projects.filter(
          (p) => Array.isArray(p.tags) && p.tags.some((t) => String(t).trim().toLowerCase() === tagFilter),
        );
        setTagError(hits.length ? '' : `No projects are tagged "${tagFilter}".`);
        setTaggedProjects(sortProjects(hits, sort.type, sort.direction as 'asc' | 'desc'));
      })
      .catch((e) => setTagError((e as Error).message || 'Could not load the tag list.'));
  }, [tagFilter, sort.type, sort.direction]);

  const menuItems: DropdownItemsType[] = dropdownItems.concat(
    tagFilter ? [{ label: 'all tags (clear filter)', value: `${TAG_PREFIX}` }] : [],
    tagNames.map((t) => ({ label: `tag: ${t}`, value: `${TAG_PREFIX}${t}` })),
  );

  const openDialog = () => {
    setShowDialog(true);
  };
  const closeDialog = () => {
    setShowDialog(false);
  };

  const filterProjects = async (
    budgetLow: number,
    budgetHigh: number,
    dateLow: Date,
    dateHigh: Date,
    completeState: 'complete' | 'incomplete' | 'all',
  ) => {
    setFilter({
      budgetLow: budgetLow,
      budgetHigh: budgetHigh,
      dateLow: dateLow,
      dateHigh: dateHigh,
      completeState: completeState,
    });
  };

  useEffect(() => {
    const projectId = sessionStorage.getItem('lastClickedProjectId');
    if (projectId) {
      const index = filteredProjects.findIndex((project) => project.id === projectId);
      if (index !== -1 && itemRefs.current[index]) {
        itemRefs.current[index]!.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          sessionStorage.removeItem('lastClickedProjectId');
          sessionStorage.removeItem('length');
        }, 1000);
      }
    }
  }, [filteredProjects]);
  // Enter in the search box: the grid shows every match, best first, until the search is cleared.
  const setSearchedProjects = (projects: IProject[], query: string) => {
    setSearchedItems(projects);
    setSearchQuery(query);
  };

  const add = async () => {
    const id = await addProject();
    dispatch(push(`/project/${id}`));
  };

  function menuSelect(value: string) {
    if (value.indexOf(TAG_PREFIX) === 0) {
      const tag = value.slice(TAG_PREFIX.length);
      setTagFilter(tag || null);
      return;
    }
    const item = dropdownItems.find((i) => i.value === value);
    if (item) sortSelect(item.label);
  }

  function sortSelect(item: string) {
    let type: string = 'date';
    let direction: 'asc' | 'desc' = 'desc';

    switch (item) {
      case 'newest':
        break;
      case 'oldest':
        direction = 'asc';
        break;
      case 'title (a-z)':
        type = 'title';
        direction = 'asc';
        break;
      case 'title (z-a)':
        type = 'title';
        direction = 'desc';
        break;
      case 'client (a-z)':
        type = 'client';
        direction = 'asc';
        break;
      case 'client (z-a)':
        type = 'client';
        direction = 'desc';
        break;
      case 'budget low':
        type = 'budget';
        direction = 'asc';
        break;
      case 'budget high':
        type = 'budget';
        direction = 'desc';
        break;
      case 'agency (a-z)':
        type = 'agency';
        direction = 'asc';
        break;
      case 'agency (z-a)':
        type = 'agency';
        direction = 'desc';
        break;
      default:
        break;
    }

    if (type !== sort.type || direction !== sort.direction) {
      setSort({ type, direction });
    }
  }

  const goToProject = (project: IProject) => {
    dispatch(setProject(project));
    dispatch(push(`/project/${project.id}`));
    closeDialog();
  };

  const getSelected = (): DropdownItemsType => {
    const selectedItem = dropdownItems.find((item) => item.value === `${sort.type} ${sort.direction}`) || dropdownItems[0];
    if (tagFilter) return { label: `${selectedItem.label} · tag: ${tagFilter}`, value: `${TAG_PREFIX}${tagFilter}` };
    return selectedItem;
  };

  return (
    <>
    {/* Drifting archive dust behind the page (Aaron, 5 Nov 2026). The content sits above it. */}
    <DustBackground />
    <Box
      sx={{ position: 'relative', zIndex: 1, maxWidth: 1376, mx: 'auto', px: ['16px', '32px'], pt: '24px', pb: '16px', minHeight: 'calc(100vh - 160px)', fontFamily: ui.font }}
    >
      {/* UI refresh title row: counts, sort/tag menu, New project */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: '1 1 320px' }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: ui.ink }}>Projects</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: ui.muted }}>
            {total.toLocaleString()} projects ·{' '}
            <button
              type="button"
              onClick={openDialog}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: ui.link, cursor: 'pointer' }}
            >
              {active.toLocaleString()} incomplete
            </button>
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: ui.muted }} title="Card size">
            Size
            <span style={{ width: 90 }}>
              <Slider onUpdate={setScale} min={0.5} max={1} />
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: ui.muted }}>
            Sort
            <DropDownMenu
              selected={getSelected()}
              menuItems={menuItems}
              onSelect={(item) => menuSelect(item.value)}
              width={'260px'}
            />
          </span>
          <button type="button" className="archy-btn" onClick={add} style={button('primary')}>
            <PlusIcon />
            New project
          </button>
        </div>
      </div>
      <ProjectFilter setFilter={filterProjects} setSearchedProjects={setSearchedProjects} />
      {attention && (
        <Flex mt={[2]} alignItems="center" sx={{ gap: '10px', flexWrap: 'wrap' }}>
          <Box as="span" sx={{ bg: '#FEF4D8', color: '#7A5300', fontWeight: 600, borderRadius: '999px', px: '12px', py: '6px', fontSize: '14px' }}>
            Needs attention: {ATTENTION_LABEL[attention]} · {attentionList ? attentionList.length.toLocaleString() : '…'} project
            {attentionList && attentionList.length === 1 ? '' : 's'}
          </Box>
          <Button variant="outlineOnWhite" onClick={() => dispatch(push('/'))}>
            Clear
          </Button>
        </Flex>
      )}
      {tagFilter && (
        <Flex mt={[2]} alignItems="center" sx={{ gap: '10px', flexWrap: 'wrap' }}>
          <Box
            as="span"
            sx={{ bg: 'grey3', color: 'white', borderRadius: '999px', px: '12px', py: '6px', fontSize: '14px' }}
          >
            Tag: {tagFilter} · {taggedProjects.length} project{taggedProjects.length === 1 ? '' : 's'}
          </Box>
          <Button variant="outlineOnWhite" onClick={() => setTagFilter(null)}>
            Clear tag filter
          </Button>
          {tagError && (
            <Box as="span" color="primary">
              {tagError}
            </Box>
          )}
        </Flex>
      )}
      {/* Same gap above the cards as between them (Aaron, 5 Nov 2026). */}
      <Box height={CARD_GAP} />
      <InfiniteScroll
        dataLength={filteredProjects.length}
        next={() => {
          loadMore();
        }}
        hasMore={true}
        loader={<></>}
        scrollThreshold={0.9}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${300 * scale}px, 1fr))`,
            gridGap: `${CARD_GAP}px`,
          }}
        >
          {projectsLoading ? (
            <Flex justifyContent="center" alignItems="center" minHeight={300}>
              <Spinner altStyle />
            </Flex>
          ) : (
            <>
              {showDialog && (
                <Dialog title="Incomplete Projects" exit={closeDialog}>
                  <IncompleteList incompleteProjects={incompleteProjects} gotoProject={goToProject} />
                </Dialog>
              )}
              {attention && attentionList
                ? attentionList.map((project) => <ProjectContainer key={project.id} project={project} scale={scale} />)
                : tagFilter
                ? taggedProjects.map((project) => <ProjectContainer key={project.id} project={project} scale={scale} />)
                : searchQuery
                ? searchedItems.map((project) => <ProjectContainer key={project.id} project={project} scale={scale} />)
                : filteredProjects.map((project, index) => (
                    <div ref={(el) => (itemRefs.current[index] = el)}>
                      <ProjectContainer
                        key={project.id}
                        project={project}
                        scale={scale}
                        setProjectsLength={() => {
                          sessionStorage.setItem('length', filteredProjects.length.toString());
                        }}
                      />
                    </div>
                  ))}
            </>
          )}
        </Box>
      </InfiniteScroll>
      {!projectsLoading && !attention && !tagFilter && !searchQuery && filteredProjects.length < total && (
        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center', gap: 10 }}>
          <button type="button" className="archy-btn" onClick={() => loadMore()} style={button('plain')}>
            Load more
          </button>
          <button type="button" className="archy-btn" onClick={loadAllProjects} style={button('quiet')}>
            Load all {total.toLocaleString()}
          </button>
        </div>
      )}
    </Box>
    </>
  );
};

export default Home;
